/**
 * ==========================================================================
 *  tourDistanz.js — Entfernungen zwischen aufeinanderfolgenden Tour-Stopps
 * ==========================================================================
 *  Zwei Modi:
 *   - Luftlinie (Haversine): sofort, offline, immer verfuegbar. Grober
 *     Richtwert; unterschaetzt die echte Fahrstrecke meist um ~25-40 %,
 *     liefert KEINE Fahrzeit.
 *   - Strasse (OSRM): echte Fahrstrecke + Fahrzeit ueber den offenen
 *     Routing-Dienst router.project-osrm.org. Laeuft im Browser (wie die
 *     Nominatim-Ortssuche auf der Karte), kostenlos, kein API-Key. Ein
 *     einziger Request liefert alle Teilstrecken der Tour auf einmal.
 *     Bei Nichterreichbarkeit faellt die Anzeige auf Luftlinie zurueck.
 *
 *  Ein "Stopp" ist ein Objekt mit { id, lat, lng } in Tour-Reihenfolge.
 *  Rueckgabe jeweils:
 *    {
 *      quelle: 'luftlinie' | 'strasse',
 *      proStopp: { <id>: { vorherKm, nachherKm, vorherMin, nachherMin } },
 *      gesamtKm, gesamtMin   // gesamtMin nur bei 'strasse'
 *    }
 *  Werte, die nicht bestimmbar sind (Randstopp, fehlende Koordinaten),
 *  sind null.
 * ==========================================================================
 */

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (g) => (g * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Nur Stopps mit gueltigen Koordinaten, in gegebener Reihenfolge.
function mitKoords(stopps) {
  return (stopps || []).filter(
    (s) => typeof s.lat === 'number' && typeof s.lng === 'number'
  );
}

// Baut aus Teilstrecken-Arrays (km + optional min) die proStopp-Zuordnung.
// legKm[i] / legMin[i] ist die Strecke von coord[i] -> coord[i+1].
function baueProStopp(coord, legKm, legMin) {
  const proStopp = {};
  coord.forEach((s, i) => {
    proStopp[s.id] = {
      vorherKm: i > 0 ? legKm[i - 1] : null,
      nachherKm: i < coord.length - 1 ? legKm[i] : null,
      vorherMin: legMin && i > 0 ? legMin[i - 1] : null,
      nachherMin: legMin && i < coord.length - 1 ? legMin[i] : null,
    };
  });
  return proStopp;
}

/**
 * Luftlinien-Entfernungen — synchron, immer verfuegbar.
 */
export function luftlinienStrecken(stopps) {
  const coord = mitKoords(stopps);
  const legKm = [];
  for (let i = 0; i < coord.length - 1; i++) {
    legKm.push(
      haversineKm(coord[i].lat, coord[i].lng, coord[i + 1].lat, coord[i + 1].lng)
    );
  }
  const gesamtKm = legKm.reduce((a, b) => a + b, 0);
  return {
    quelle: 'luftlinie',
    proStopp: baueProStopp(coord, legKm, null),
    gesamtKm,
    gesamtMin: null,
  };
}

/**
 * Strassen-Entfernungen + Fahrzeit via OSRM. Async; wirft bei Fehler,
 * damit der Aufrufer auf Luftlinie zurueckfallen kann. Ein Request fuer
 * die ganze Tour. Optionaler AbortSignal, um veraltete Anfragen zu killen.
 */
export async function strassenStrecken(stopps, signal) {
  const coord = mitKoords(stopps);
  if (coord.length < 2) {
    return { quelle: 'strasse', proStopp: {}, gesamtKm: 0, gesamtMin: 0 };
  }
  const coords = coord.map((s) => `${s.lng},${s.lat}`).join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false&annotations=false`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);
  const j = await res.json();
  if (j.code !== 'Ok' || !j.routes || !j.routes[0] || !j.routes[0].legs) {
    throw new Error(`OSRM: ${j.code || 'keine Route'}`);
  }
  const legs = j.routes[0].legs; // Laenge = coord.length - 1
  const legKm = legs.map((l) => l.distance / 1000);
  const legMin = legs.map((l) => l.duration / 60);
  return {
    quelle: 'strasse',
    proStopp: baueProStopp(coord, legKm, legMin),
    gesamtKm: legKm.reduce((a, b) => a + b, 0),
    gesamtMin: legMin.reduce((a, b) => a + b, 0),
  };
}

// Signatur der geordneten Koordinaten — aendert sich nur, wenn sich die
// Stopp-Menge ODER ihre Reihenfolge aendert. Damit laesst sich die OSRM-
// Abfrage gezielt neu ausloesen (z.B. nach Uhrzeit-Umsortierung), aber
// nicht bei jedem Re-Render.
export function streckenSignatur(stopps) {
  return mitKoords(stopps)
    .map((s) => `${s.id}:${s.lat.toFixed(5)},${s.lng.toFixed(5)}`)
    .join('|');
}

// ---- Formatierung fuer die Anzeige -------------------------------------

export function fmtKm(km) {
  if (km == null) return '–';
  return `${km.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;
}

export function fmtMin(min) {
  if (min == null) return null;
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

// ==========================================================================
//  Assistierte Tourenplanung: Start/Ziel, Reihenfolge-Optimierung, Zeitplan
// ==========================================================================

// Fester Start- und Zielpunkt aller Touren (Buero). Koordinaten werden beim
// ersten Bedarf per Nominatim ermittelt und im localStorage gecacht; der
// Fallback stellt sicher, dass die Planung auch ohne Geocoder grob stimmt.
export const TOUR_START = {
  label: 'Friedrich-Karl-Straße 90, 28205 Bremen',
  query: 'Friedrich-Karl-Straße 90, 28205 Bremen',
  fallbackLat: 53.0770,
  fallbackLng: 8.8500,
};

// Beliebige Adresse -> Koordinaten (Nominatim im Browser + localStorage-Cache).
// Liefert null, wenn die Adresse nicht aufloesbar ist (ausser der Standard-
// Bueroadresse, fuer die es einen Fallback gibt).
export async function adresseKoordinaten(query) {
  const q = (query || '').trim();
  if (!q) return null;
  const cacheKey = 'tour_geocode:' + q.toLowerCase();
  try {
    const roh = localStorage.getItem(cacheKey);
    if (roh) {
      const c = JSON.parse(roh);
      if (typeof c.lat === 'number' && typeof c.lng === 'number') {
        return { lat: c.lat, lng: c.lng, label: q, quelle: 'cache' };
      }
    }
  } catch { /* localStorage evtl. nicht verfuegbar */ }

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'de' } });
    if (res.ok) {
      const j = await res.json();
      if (Array.isArray(j) && j[0] && j[0].lat && j[0].lon) {
        const lat = parseFloat(j[0].lat);
        const lng = parseFloat(j[0].lon);
        try { localStorage.setItem(cacheKey, JSON.stringify({ lat, lng })); } catch { /* egal */ }
        return { lat, lng, label: q, quelle: 'nominatim' };
      }
    }
  } catch { /* Netz/Geocoder nicht erreichbar */ }

  // Fallback nur fuer die Standard-Bueroadresse.
  if (q === TOUR_START.query || q === TOUR_START.label) {
    return { lat: TOUR_START.fallbackLat, lng: TOUR_START.fallbackLng, label: TOUR_START.label, quelle: 'fallback' };
  }
  return null;
}

// Koordinaten der Standard-Bueroadresse (Start/Ziel).
export async function startKoordinaten() {
  const r = await adresseKoordinaten(TOUR_START.query);
  return r ? { ...r, label: TOUR_START.label } : null;
}

// Nearest-Neighbor-Reihenfolge ab Startpunkt (Luftlinie als Naehe-Mass —
// schnell und fuer eine Tagestour voellig ausreichend). Liefert die Stopps
// mit Koordinaten in optimierter Reihenfolge; Stopps ohne Koordinaten kommen
// unveraendert hinten dran (koennen nicht geroutet werden).
export function optimiereReihenfolge(start, stopps) {
  const mitKo = mitKoords(stopps);
  const ohneKo = (stopps || []).filter(s => !(typeof s.lat === 'number' && typeof s.lng === 'number'));
  const rest = [...mitKo];
  const order = [];
  let curLat = start.lat, curLng = start.lng;
  while (rest.length) {
    let best = 0, bestD = Infinity;
    for (let i = 0; i < rest.length; i++) {
      const d = haversineKm(curLat, curLng, rest[i].lat, rest[i].lng);
      if (d < bestD) { bestD = d; best = i; }
    }
    const s = rest.splice(best, 1)[0];
    order.push(s);
    curLat = s.lat; curLng = s.lng;
  }
  return { geordnet: order, ohneKoordinaten: ohneKo };
}

function _minToHHMM(min) {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${String(h).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}
function _hhmmToMin(s) {
  if (!s || !/^\d{1,2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Baut den kompletten Tour-Vorschlag: optimierte Reihenfolge + Fahrstrecken/
 * -zeiten (OSRM) + Ankunfts-/Abfahrtszeiten + Konflikt-Markierung.
 *
 * stopps: [{ id, lat, lng, name, uhrzeit(optional 'HH:MM'), status }]
 * opts:   { start, ziel, startzeit:'HH:MM', dauerMin, pufferMin }
 *   start / ziel: je { lat, lng, label } ODER null (dann nicht Teil der Route).
 *     - start gesetzt: Route beginnt dort.
 *     - start null: Route beginnt beim ersten Kunden (Startzeit = Ankunft dort).
 *     - ziel gesetzt: Route endet dort (letzte Fahrt zum Ziel wird gezeigt).
 *     - ziel null: keine Schluss-Fahrt.
 * Wirft bei OSRM-Fehler -> Aufrufer kann reagieren.
 */
export async function berechneTourVorschlag(stopps, opts, signal) {
  const start = opts?.start || null;
  const ziel = opts?.ziel || null;
  const dauer = opts?.dauerMin ?? 30;
  const puffer = opts?.pufferMin ?? 10;
  const startMin = _hhmmToMin(opts?.startzeit) ?? 9 * 60;

  // Reihenfolge bestimmen. Seed: Start, sonst Ziel (dann umkehren, damit die
  // Tour am Ziel endet), sonst erster Kunde mit Koordinaten.
  const mitKo = mitKoords(stopps);
  let geordnet;
  if (start) {
    geordnet = optimiereReihenfolge(start, stopps).geordnet;
  } else if (ziel) {
    geordnet = optimiereReihenfolge(ziel, stopps).geordnet.reverse();
  } else {
    geordnet = mitKo.length ? optimiereReihenfolge(mitKo[0], stopps).geordnet : [];
  }
  const ohneKoordinaten = (stopps || []).filter(
    s => !(typeof s.lat === 'number' && typeof s.lng === 'number')
  );

  // Punktfolge fuer OSRM: (Start?) -> alle Stopps -> (Ziel?).
  const punkte = [...(start ? [start] : []), ...geordnet, ...(ziel ? [ziel] : [])];
  if (punkte.length < 2) {
    throw new Error('Zu wenige Punkte fuer eine Route.');
  }
  const coords = punkte.map(p => `${p.lng},${p.lat}`).join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=false&annotations=false`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);
  const j = await res.json();
  if (j.code !== 'Ok' || !j.routes?.[0]?.legs) throw new Error(`OSRM: ${j.code || 'keine Route'}`);
  const legKm = j.routes[0].legs.map(l => l.distance / 1000);
  const legMin = j.routes[0].legs.map(l => l.duration / 60);

  const offset = start ? 1 : 0;   // Index-Versatz: seq[0] ist Start (falls vorhanden)
  let t = startMin;
  const plan = geordnet.map((s, i) => {
    const ci = offset + i;                    // Position im seq-Array
    const einfahrtLeg = ci - 1;               // Leg, das ZU diesem Kunden fuehrt
    const fahrtKm = einfahrtLeg >= 0 ? legKm[einfahrtLeg] : null;
    const fahrtMinRoh = einfahrtLeg >= 0 ? legMin[einfahrtLeg] : null;
    if (fahrtMinRoh != null) t += fahrtMinRoh + puffer;   // Anfahrt (+ Puffer)
    const ankunftMin = t;
    t += dauer;                               // Beratung
    const fixMin = _hhmmToMin(s.uhrzeit);
    const konflikt = (s.status === 'bestaetigt' && fixMin != null)
      ? Math.abs(fixMin - ankunftMin) > 20
      : false;
    return {
      ...s,
      fahrtKm, fahrtMin: fahrtMinRoh,
      ankunft: _minToHHMM(ankunftMin),
      abfahrt: _minToHHMM(t),
      konflikt,
      fixUhrzeit: s.uhrzeit || null,
    };
  });

  // Schluss-Fahrt zum Ziel (falls vorhanden).
  let endeMin = t;
  let rueckKm = null, rueckMin = null;
  if (ziel && legMin.length) {
    rueckKm = legKm[legKm.length - 1];
    rueckMin = legMin[legMin.length - 1];
    endeMin = t + rueckMin + puffer;
  }

  return {
    plan,
    ohneKoordinaten,
    gesamtKm: legKm.reduce((a, b) => a + b, 0),
    gesamtFahrMin: legMin.reduce((a, b) => a + b, 0),
    rueckfahrtKm: rueckKm,
    rueckfahrtMin: rueckMin,
    ende: _minToHHMM(endeMin),
    startLabel: start ? start.label : null,
    zielLabel: ziel ? ziel.label : null,
  };
}
